import { useEffect, useMemo, useState } from "react";
import { ListFieldsType, myTaskContactsListName, myTaskInstructionsFilesLibName, siteUrl } from "src/api/config";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { useFilePreview, isPreviewable } from "src/hooks/useFilePreview";
import EyeIcon from "src/components/ui-icons/reports/EyeIcon";
import Loading from "src/components/Loading";
import HelpIcon from "src/components/ui-icons/menu/HelpIcon";
import { DownloadIcon, FileIcon } from "src/components/ui-icons/upload";
import { siteUrlOrSiteApi } from "src/api/config/systemSwitch";
import { Modal } from "src/components/DialogComponent";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";
import { getItems } from "src/modules/spItems/actions";
import { selectItems } from "src/modules/spItems/selectors";
import { FileType } from "src/types/main";
import { isPWA } from "src/utils/mobile";
import { downloadForApp } from "src/utils/downloadForApp";

const siteUrlProd = 'https://supply.napitkivmeste.ru'; // для корректного отображения превью

export default function MTHelpPageMobile() {
  const dispatch = useAppDispatch();
  const contacts = useAppSelector(
    (state) => selectItems(state, myTaskContactsListName)
  ) as ListFieldsType[typeof myTaskContactsListName][];
  const instructions = useAppSelector(
    (state) => selectItems(state, myTaskInstructionsFilesLibName)
  ) as FileType[];

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState<string>("");
  const [viewerObjectUrl, setViewerObjectUrl] = useState<string | null>(null);
  const [viewerBlob, setViewerBlob] = useState<Blob | null>(null);
  const [viewerSourceUrl, setViewerSourceUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const { openPreview, previewLoading, previewFileId, PreviewModal } = useFilePreview(siteUrl, siteUrlProd);
  const isPreviewableDoc = (file: FileType) =>
    isPreviewable(file.FileRef) || /\.(pdf)$/.test((file.FileLeafRef || "").toLowerCase());

  const toRelativePath = (ref: string) => {
    try {
      return new URL(ref).pathname.replace(/\/{2,}/g, "/");
    } catch {
      return ref;
    }
  };

  const handleEyeClick = (file: FileType) => {
    if (/\.pdf$/.test((file.FileLeafRef || "").toLowerCase())) {
      openViewer(file);
    } else {
      openPreview(toRelativePath(file.FileRef), file.ID);
    }
  };

  useEffect(() => {
    dispatch(getItems({ listName: myTaskContactsListName }));
    dispatch(getItems({ listName: myTaskInstructionsFilesLibName }));
  }, [dispatch]);

  const revokeViewerUrl = () => {
    if (viewerObjectUrl) {
      try {
        URL.revokeObjectURL(viewerObjectUrl);
      } catch {
        // ignore
      }
    }
    setViewerObjectUrl(null);
    setViewerBlob(null);
    setViewerSourceUrl(null);
  };

  const isIOS = useMemo(() => {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const iOSDevice = /iPad|iPhone|iPod/.test(ua);
    const iPadOS = ua.includes("Mac") && "ontouchend" in document;
    return iOSDevice || iPadOS;
  }, []);

  const forceDownloadUrl = (url: string) => {
    if (!url || url === "#") return url;
    // SharePoint / iOS: forcing `download=1` avoids opening Quick Look preview for Office files.
    if (url.includes("download=1")) return url;
    return url.includes("?") ? `${url}&download=1` : `${url}?download=1`;
  };

  const buildFetchUrl = useMemo(() => {
    return (ref: string) => {
      if (!ref) return "";
      let trimmed = ref.trim();
      if (trimmed.startsWith("https//")) trimmed = "https://" + trimmed.slice("https//".length);
      else if (trimmed.startsWith("http//")) trimmed = "http://" + trimmed.slice("http//".length);

      if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
      return siteUrlOrSiteApi + encodeURI(trimmed);
    };
  }, []);

  const buildInstructionUrl = (file: FileType) => {
    if (!file.FileRef) return "#";

    let ref = file.FileRef.trim();

    // Исправляем возможные варианты без двоеточия
    if (ref.startsWith("https//")) {
      ref = "https://" + ref.slice("https//".length);
    } else if (ref.startsWith("http//")) {
      ref = "http://" + ref.slice("http//".length);
    }

    // Если это уже абсолютная ссылка, возвращаем как есть
    if (ref.startsWith("http://") || ref.startsWith("https://")) {
      return forceDownloadUrl(ref);
    }

    // Иначе считаем, что это относительный путь к порталу
    return forceDownloadUrl(siteUrlOrSiteApi + encodeURI(ref));
  };

  const openViewer = async (file: FileType) => {
    setViewerError(null);
    setShareError(null);
    setViewerTitle(file.FileLeafRef || "Инструкция");
    setViewerOpen(true);
    setViewerLoading(true);

    revokeViewerUrl();

    try {
      const url = buildFetchUrl(file.FileRef || "");
      if (!url) throw new Error("Нет ссылки на файл");
      setViewerSourceUrl(url);

      const name = (file.FileLeafRef || "").toLowerCase();
      const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
      const previewable =
        ext === ".pdf" ||
        ext === ".png" ||
        ext === ".jpg" ||
        ext === ".jpeg" ||
        ext === ".gif" ||
        ext === ".webp" ||
        ext === ".svg";

      // For non-previewable types (xlsx/docx/etc) do not fetch in browser/webview:
      // - we can't render them inline anyway
      // - fetch may be blocked by CSP/CORS and just shows "Failed to fetch"
      if (!previewable) {
        setViewerLoading(false);
        return;
      }

      try {
        const response = await fetch(url, { credentials: "include" });
        if (!response.ok) throw new Error(`Ошибка загрузки файла (${response.status})`);

        const blob = await response.blob();
        setViewerBlob(blob);

        const objectUrl = URL.createObjectURL(blob);
        setViewerObjectUrl(objectUrl);
      } catch {
        // Fallback: still try to render via direct URL in iframe/img.
        // Blob-based "Save" may be unavailable in some environments.
        setShareError("Не удалось загрузить файл для сохранения. Используйте «Открыть в браузере».");
      } finally {
        setViewerLoading(false);
      }
    } catch (err: any) {
      setViewerError(err?.message || "Не удалось открыть инструкцию");
      setViewerLoading(false);
    }
  };

  const shareInstruction = async () => {
    setShareError(null);
    if (!viewerBlob) {
      setShareError("Файл ещё не загружен");
      return;
    }
    const nav: any = navigator as any;
    if (!nav?.share) {
      setShareError("Сохранение недоступно в этой версии приложения. Откройте инструкцию в браузере.");
      return;
    }
    try {
      const file = new File([viewerBlob], viewerTitle || "instruction", {
        type: viewerBlob.type || "application/octet-stream",
        lastModified: Date.now(),
      });
      if (nav?.canShare && !nav.canShare({ files: [file] })) {
        setShareError("Сохранение недоступно в этой версии приложения. Откройте инструкцию в браузере.");
        return;
      }
      await nav.share({ title: viewerTitle || "Инструкция", files: [file] });
    } catch (err: any) {
      if (err?.name === "AbortError") return;
      setShareError("Не удалось открыть меню «Поделиться». Попробуйте ещё раз.");
    }
  };

  const handleInstructionClick = async (e: any, file: FileType) => {
    // PWA — оставляем стандартное поведение
    if (isPWA()) return;

    // In browsers we keep the default behavior (download/open via href).
    // In iOS WebView we intercept and open viewer to avoid "blank screen" after downloads.
    if (isIOS) {
      e.preventDefault();
      e.stopPropagation();

      await openViewer(file);
      return;
    }

    // Application
    e.preventDefault();
    e.stopPropagation();

    await downloadForApp(file);
  };

  return (
    <div>
      <div className="py-[26px] mx-6 sm:mx-7.5 border-b border-[#DFE4EA]-500">
        <div className="flex items-center gap-2 w-fit">
          <span>
            <HelpIcon />
          </span>
          <span>Помощь</span>
        </div>
      </div>

      <div>
         <div className="flex flex-col gap-2 mb-2 sm:grid sm:grid-cols-2 sm:gap-2 sm:mb-5">
          {contacts.map((el) => (
            <div key={el.ID} className="p-4 border rounded-lg bg-white shadow-custom w-full gap-2 flex flex-col">
              <h4 className="text-black text-[13px] font-medium leading-4">{el.Title}</h4>
              <div className="text-[13px]">
                <span>Телефон: </span>
                <a className="text-darkblue break-word" href={`tel:${el.Phone}`}>{el.Phone}</a>
              </div>
              <div className="text-[13px]">
                <span>Адрес: </span>
                <a className="text-darkblue break-word" href={`mailto:${el.Email}`}>{el.Email}</a>
              </div>
            </div>
          ))}
        </div>

        <div className="p-4 border rounded-lg bg-white shadow-custom w-full">
          <h4 className="text-black text-[13px] font-medium leading-6 mb-2.5">Инструкции</h4>
          <FlexCol className="gap-3">
            <div className="flex flex-row">
              <div className="flex flex-col gap-y-3 w-full">
                {instructions.map((file) => (
                  <a
                    href={buildInstructionUrl(file)}
                    onClick={(e) => handleInstructionClick(e, file)}
                    download={file.FileLeafRef}
                    rel="noreferrer"
                    key={file.ID}
                    className="flex flex-row justify-between gap-2.5 px-2 py-2 rounded-lg cursor-pointer upload bg-sp-gray"
                  >
                    <div className="flex flex-col grow">
                      <div className="flex flex-row gap-x-2.5">
                        <div className="flex flex-col items-center justify-center">
                          <FileIcon />
                        </div>
                        <div className="flex flex-col text-[14px] break-word">{file.FileLeafRef}</div>
                      </div>
                    </div>

                    <FlexRow className="gap-3 items-center">
                      {isPreviewableDoc(file) && (
                        <div
                          className="flex flex-col items-center justify-center"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEyeClick(file);
                          }}
                        >
                          {previewLoading && previewFileId === file.ID ? (
                            <FlexRow className="w-6 h-6">
                              <Loading />
                            </FlexRow>
                          ) : (
                            <EyeIcon />
                          )}
                        </div>
                      )}
                      <div className="flex flex-col items-center justify-center download-icon">
                        <DownloadIcon />
                      </div>
                    </FlexRow>
                  </a>
                ))}
              </div>
            </div>
          </FlexCol>
        </div>
      </div>

      <Modal
        open={viewerOpen}
        toggleDialog={(b) => {
          if (!b) {
            setViewerOpen(false);
            setViewerLoading(false);
            setViewerError(null);
            setShareError(null);
            revokeViewerUrl();
          } else {
            setViewerOpen(true);
          }
        }}
        zIndex={120}
        closeOnBackdrop
        modalClassName="p-0 w-full sm:min-w-[600px] max-w-[calc(100vw-5%)] max-h-[calc(100dvh-80px)] overflow-hidden"
      >
        <header className="px-6 pt-6 pb-4 border-b flex flex-col gap-3">
          <h2 className="text-base font-medium text-black break-words">{viewerTitle}</h2>
          <div className="flex items-center justify-end gap-4">
            <button
              type="button"
              className="text-sm text-[#012169] underline disabled:opacity-40"
              onClick={shareInstruction}
              disabled={viewerLoading || !!viewerError || !viewerBlob}
            >
              Сохранить
            </button>
            {viewerSourceUrl && (
              <a
                className="text-sm text-[#012169] underline"
                href={forceDownloadUrl(viewerSourceUrl)}
                target="_blank"
                rel="noopener noreferrer"
              >
                Открыть в браузере
              </a>
            )}
          </div>
        </header>

        <div className="px-6 py-4 overflow-auto">
          {viewerLoading && <div className="text-[#637381]">Загрузка...</div>}
          {viewerError && <div className="text-[#BA0C2F] mb-3">{viewerError}</div>}
          {shareError && <div className="text-[#BA0C2F] mb-3">{shareError}</div>}

          {!viewerLoading && !viewerError && (
            <>
              {(() => {
                const name = (viewerTitle || "").toLowerCase();
                const ext = name.includes(".") ? name.slice(name.lastIndexOf(".")) : "";
                const isPdf = ext === ".pdf";
                const isImage =
                  ext === ".png" || ext === ".jpg" || ext === ".jpeg" || ext === ".gif" || ext === ".webp" || ext === ".svg";

                const src = viewerObjectUrl || viewerSourceUrl || null;
                if (!src) return null;

                if (isImage) return <img src={src} alt={viewerTitle} className="w-full h-auto select-none" />;
                if (isPdf) return <iframe title={viewerTitle} src={src} className="w-full" style={{ height: "70vh", border: "none" }} />;

                return (
                  <div className="text-[#637381]">
                    Этот тип файла нельзя отобразить внутри приложения. Нажмите «Открыть в браузере».
                  </div>
                );
              })()}
            </>
          )}
        </div>
      </Modal>

      {PreviewModal}

    </div>
  );
}
